/**
 * Represents a virtualized collection list component.
 * This custom Web Component dynamically loads, renders, and manages large lists
 * of items efficiently by employing virtualization techniques.
 *
 * The component observes grid items for visibility and replaces placeholder elements
 * with actual content as they enter the viewport. It also supports lazy-loading
 * additional content when scrolled near the end of the list.
 *
 * The following main features are provided:
 * - Virtualization: Replaces off-screen items with placeholders to optimize memory usage and rendering performance.
 * - Resize observation: Tracks changes in item dimensions for proper layout adjustments.
 * - Lazy loading: Dynamically fetches and appends more items as the user scrolls down.
 * - Gallery initialization: Automatically initializes product card galleries when relevant items become visible.
 * - Event handling: Sets up interaction events for items, such as click handling for swatches and wishlist additions.
 *
 * Extends:
 * - HTMLElement
 */
class CollectionVirtualList extends HTMLElement {

  /**
   * Initializes and sets up the necessary observers and data structures
   * for managing product grid elements, including resize behavior, lazy loading,
   * and card virtualization.
   *
   * The method performs the following tasks:
   * - Sets up a `ResizeObserver` to track size changes for grid items.
   * - Initializes a `Map` to store measurement data such as item height and column span.
   * - Observes grid items for intersection changes to handle lazy initialization of galleries.
   * - Implements virtualization by replacing elements with placeholders when they are out of view.
   * - Configures the loading of additional content when scrolling to a predefined anchor.
   * - Watches for child list mutations to dynamically observe new loading anchors.
   *
   * @return {void} This method does not return any value.
   */
  connectedCallback() {
    this._cardStore = [];
    this._measureStore = new Map();
    this._isLoading = false;

    const container = this.querySelector('#ProductGridContainer');
    const gridElement = this.querySelector('#product-grid');
    if (!gridElement) { return; }

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const index = Number(entry.target.dataset.virtualIndex);
        if (Number.isNaN(index)) { continue; }
        const { offsetWidth: width, offsetHeight: height } = entry.target;
        if (!width || !height) { continue; }
        this._measureStore.set(index, {
          ratio: height / width,
          colSpan: entry.target.classList.contains('grid__item--double-size') ? 2 : 1,
        });
      }
    });

    const initialItems = [...gridElement.querySelectorAll(':scope > .grid__item')];
    initialItems.forEach((item, index) => {
      item.dataset.virtualIndex = index;
      item._initialized = true;
      this._cardStore.push(item);
      this._resizeObserver.observe(item);
    });

    this._galleryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) { return; }
          const gallerySwiper = entry.target.querySelector(
            '.product-card-gallery-swiper:not(.swiper-initialized)',
          );
          if (gallerySwiper) { initProductCardSwiper(gallerySwiper); }
          this._galleryObserver.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.5 },
    );
    initialItems.forEach((item) => this._galleryObserver.observe(item));

    this._virtualizationObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          const index = Number(el.dataset.virtualIndex);
          if (Number.isNaN(index)) { continue; }
          const isPlaceholder = el.classList.contains('grid__item--placeholder');

          if (entry.isIntersecting && isPlaceholder) {
            const realCard = this._cardStore[index];
            el.replaceWith(realCard);
            this._virtualizationObserver.unobserve(el);
            this._virtualizationObserver.observe(realCard);
            if (!realCard._initialized) {
              this._initCardEvents(realCard);
              realCard._initialized = true;
            }
            const gallery = realCard.querySelector('.product-card-gallery-swiper');
            if (gallery?.swiper) {
              gallery.swiper.update();
            } else if (gallery && !gallery.classList.contains('swiper-initialized')) {
              initProductCardSwiper(gallery);
            }
          } else if (!entry.isIntersecting && !isPlaceholder) {
            const dims = this._measureStore.get(index);
            if (dims) {
              const ph = this._makePlaceholder(index, dims);
              el.replaceWith(ph);
              this._virtualizationObserver.unobserve(el);
              this._virtualizationObserver.observe(ph);
            }
          }
        }
      },
      { root: null, rootMargin: '800px 0px', threshold: 0 },
    );
    initialItems.forEach((item) => this._virtualizationObserver.observe(item));

    let anchor = this.querySelector('#collection-load-more-anchor');

    this._listObserver = new IntersectionObserver(
      async (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !this._isLoading) {
          this._listObserver.unobserve(anchor);
          await this._loadNextPage(container, gridElement);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    );

    if (anchor) { this._listObserver.observe(anchor); }

    this._containerObserver = new MutationObserver(() => {
      anchor = this.querySelector('#collection-load-more-anchor');
      if (anchor) { this._listObserver.observe(anchor); }
    });
    this._containerObserver.observe(container, { childList: true, subtree: false });
  }

  /**
   * Re-runs setup against the current DOM. Needed because filtering (facets.js)
   * replaces #ProductGridContainer's innerHTML in place, which never triggers
   * connectedCallback/disconnectedCallback on this element and leaves the
   * observers and _cardStore pointing at the removed nodes.
   *
   * @return {void}
   */
  reinit() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  /**
   * Lifecycle method that is called when the component is disconnected from the DOM.
   * It cleans up observers and resets internal state to prevent memory leaks and ensure proper teardown.
   *
   * @return {void} This method does not return a value.
   */
  disconnectedCallback() {
    this._virtualizationObserver?.disconnect();
    this._resizeObserver?.disconnect();
    this._listObserver?.disconnect();
    this._containerObserver?.disconnect();
    this._galleryObserver?.disconnect();
    this._cardStore = [];
    this._measureStore = new Map();
  }

  /**
   * Creates a placeholder DOM element for a grid item.
   *
   * @param {number} index - The virtual index of the placeholder.
   * @param {{ colSpan: number, ratio: number }} dims - The dimensions of the placeholder, including column span and height/width ratio.
   * @return {HTMLElement} The created placeholder list item element.
   */
  _makePlaceholder(index, dims) {
    const placeholder = document.createElement('li');
    placeholder.className =
      `grid__item${
        dims.colSpan === 2 ? ' grid__item--double-size' : ''
      } grid__item--placeholder`;
    placeholder.dataset.virtualIndex = index;
    placeholder.style.aspectRatio = `1 / ${dims.ratio}`;
    placeholder.setAttribute('aria-hidden', 'true');
    return placeholder;
  }

  /**
   * Initializes card events for the provided item.
   * Sets up the necessary event handlers and observers for the card element.
   *
   * @param {HTMLElement} item - The card element to initialize events for.
   * @return {void}
   */
  _initCardEvents(item) {
    addToWishlistEvent(item);
    this._galleryObserver.observe(item);
  }

  /**
   * Loads the next page of items and updates the container and grid element with new content.
   * Handles pagination, card initialization, and virtualized rendering.
   *
   * @param {HTMLElement} container - The parent container element where pagination and new items are managed.
   * @param {HTMLElement} gridElement - The grid element where new items will be appended.
   * @return {Promise<void>} Resolves when the next page has been successfully loaded and processed.
   */
  async _loadNextPage(container, gridElement) {
    this._isLoading = true;
    const nextLink = container.querySelector('.pagination__item--prev');
    if (!nextLink) {
      this._isLoading = false;
      return;
    }

    try {
      const [base, query] = nextLink.href.split('?');
      const view = this.dataset.showGallery === 'true' ? 'ajax-gallery' : 'ajax';
      const response = await fetch(`${base}?view=${view}&${query}`, {
        method: 'GET',
        credentials: 'same-origin',
      });
      const content = await response.text();
      const parsed = document.createElement('ul');
      parsed.innerHTML = content;

      const newPagination = parsed.querySelector('.pagination');
      const existingPagination = container.querySelector('.pagination');
      if (newPagination?.querySelector('.pagination__item--prev') == null) {
        existingPagination?.remove();
      } else if (existingPagination) {
        existingPagination.innerHTML = newPagination.innerHTML;
      }

      const startIndex = this._cardStore.length;
      const newItems = [...parsed.querySelectorAll('.grid__item')];
      const imageRatio = this.dataset.imageRatio ?? 'adapt';
      let fixedRatio = null;
      if (imageRatio === 'portrait') {
        fixedRatio = '125%';
      } else if (imageRatio === 'square') {
        fixedRatio = '100%';
      }
      newItems.forEach((node, index) => {
        if (fixedRatio) {
          node.querySelector('.card')?.style.setProperty('--ratio-percent', fixedRatio);
          node.querySelector('.card__inner')?.style.setProperty('--ratio-percent', fixedRatio);
        }
        const virtualIndex = startIndex + index;
        node.dataset.virtualIndex = virtualIndex;
        this._cardStore.push(node);
        gridElement.appendChild(node);
        this._resizeObserver.observe(node);
        this._initCardEvents(node);
        node._initialized = true;
        this._virtualizationObserver.observe(node);
      });
    } catch (err) {
      console.error('[CollectionVirtualList]', err);
    } finally {
      this._isLoading = false;
      const nextAnchor = this.querySelector('#collection-load-more-anchor');
      if (nextAnchor) { this._listObserver.observe(nextAnchor); }
    }

  }
}

customElements.define('collection-virtual-list', CollectionVirtualList);
